import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import './pendingRequisitionsModal.css';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import ApiService from '../../services/apiService';

/** API Type param only — not shown in UI. None / unset → Inventory */
export const getRequisitionTypeApiValue = (requisitionType) => {
  const label = (requisitionType?.label ?? requisitionType?.value ?? requisitionType ?? '').toString().trim();
  if (!label || label.toLowerCase() === 'none') return 'Inventory';
  return label;
};

const numberFormatter = (params) => {
  const val = params.value;
  if (val === null || val === undefined || val === '') return '';
  const num = Number(val);
  return Number.isNaN(num) ? val : num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 4 });
};

const PendingRequisitionsModal = ({ show, onHide, onApply, requisitionType }) => {
  const gridRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        suppressMovable: true,
        width: 48,
        pinned: 'left',
      },
      { headerName: 'Requisition No', field: 'requisitionNo', suppressMovable: true, width: 140 },
      { headerName: 'Sr No', field: 'srno', suppressMovable: true, width: 80 },
      { headerName: 'Item Code', field: 'itemCode', suppressMovable: true, width: 120 },
      { headerName: 'Description', field: 'itemDescription', suppressMovable: true, width: 140 },
      { headerName: 'Specification', field: 'specification', suppressMovable: true, flex: 1, minWidth: 180 },
      { headerName: 'UOM', field: 'baseUOM', suppressMovable: true, width: 80 },
      { headerName: 'Qty', field: 'baseQty', suppressMovable: true, width: 90, valueFormatter: numberFormatter },
      { headerName: 'Bal Qty', field: 'balQty', suppressMovable: true, width: 95, valueFormatter: numberFormatter },
      { headerName: 'Unit Price', field: 'unitPrice', suppressMovable: true, width: 100, valueFormatter: numberFormatter },
      { headerName: 'VAT', field: 'vatDesc', suppressMovable: true, width: 140 },
      { headerName: 'VAT %', field: 'vatPerc', suppressMovable: true, width: 90, valueFormatter: numberFormatter },
      { headerName: 'VAT Amt', field: 'vatAmount', suppressMovable: true, width: 90, valueFormatter: numberFormatter },
      { headerName: 'Delivery Date', field: 'deliveryDate', suppressMovable: true, width: 120 },
      { headerName: 'Cost Center 1', field: 'costCenter1', suppressMovable: true, width: 150 },
      { headerName: 'Cost Center 2', field: 'costCenter2', suppressMovable: true, width: 120 },
      { headerName: 'Delivery Note', field: 'deliveryNoteNo', suppressMovable: true, width: 120 },
      { headerName: 'Modified By', field: 'modifiedBy', suppressMovable: true, width: 110 },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    [],
  );

  const getRowId = useCallback((params) => `${params.data?.requisitionNo ?? ''}-${params.data?.srno ?? ''}-${params.data?.itemCode ?? ''}`, []);

  const typeKey = useMemo(() => getRequisitionTypeApiValue(requisitionType), [requisitionType?.label, requisitionType?.value, requisitionType]);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    setSelectedRows([]);
    gridRef.current?.api?.deselectAll();
    try {
      const apiService = new ApiService();
      const params = new URLSearchParams();
      params.append('Type', typeKey);
      const response = await apiService.getApiParamater('GET_PENDING_REQUISITIONS_FOR_PO', `?${params.toString()}`);
      const list = response?.data?.result ?? [];
      const normalized = Array.isArray(list) ? list : [];
      setRows(normalized);
      if (normalized.length === 0) {
        toast.info('No pending requisitions found');
      }
    } catch (error) {
      console.error('Error fetching pending requisitions:', error);
      toast.error('Failed to load pending requisitions');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [typeKey]);

  useEffect(() => {
    if (show) {
      fetchPending();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when modal opens only
  }, [show]);

  const onSelectionChanged = useCallback((event) => {
    setSelectedRows(event.api.getSelectedRows());
  }, []);

  const handleSelectAll = () => {
    gridRef.current?.api?.selectAll();
  };

  const handleDeselectAll = () => {
    gridRef.current?.api?.deselectAll();
  };

  const handleSave = () => {
    const currentSelection = gridRef.current?.api?.getSelectedRows() ?? selectedRows;
    if (!currentSelection.length) {
      toast.warning('Please select at least one requisition line');
      return;
    }
    onApply(currentSelection);
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      contentLabel="Pending Requisitions"
      className="pending-requisitions-modal"
      dialogClassName="pending-requisitions-modal-dialog"
    >
      <Modal.Header closeButton>
        <Modal.Title>Get Pending Requisitions</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="center p-4">
            <ClipLoader color="#5B93FF" />
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center pb-2">
              <small className="text-muted">Select multiple rows using the checkboxes (header checkbox selects all).</small>
              <div className="d-flex gap-2 align-items-center">
                <span className="text-muted small">{selectedRows.length} selected</span>
                <button type="button" className="mainbtnGry btn-sm" onClick={handleSelectAll} disabled={!rows.length}>
                  Select All
                </button>
                <button type="button" className="mainbtnGry btn-sm" onClick={handleDeselectAll} disabled={!selectedRows.length}>
                  Clear
                </button>
              </div>
            </div>
            <div className="gridshad">
              <div className="ag-theme-quartz" style={{ height: 'min(65vh, 560px)', width: '100%' }}>
                <AgGridReact
                  ref={gridRef}
                  rowData={rows}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  getRowId={getRowId}
                  rowSelection="multiple"
                  suppressRowClickSelection={true}
                  pagination={false}
                  onSelectionChanged={onSelectionChanged}
                  getRowStyle={(params) => (params.node.rowIndex % 2 === 0 ? { background: '#F0EDF5' } : { background: '#fff' })}
                />
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="end p-3">
        <button type="button" className="mainbtnGry" onClick={onHide}>
          Cancel
        </button>
        <button type="button" className="mainbtn" onClick={handleSave} disabled={loading || !selectedRows.length}>
          {selectedRows.length > 0 ? `Add (${selectedRows.length} selected)` : 'Add'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default PendingRequisitionsModal;
